import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { communicationService } from '../../services/communicationService';
import { getApiError } from '../../utils/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The record this email belongs to. A lead and a patient compose
   *  identically - same box, same branded shell - so one dialog serves both
   *  rather than a second copy that drifts. */
  entity?: 'patient' | 'lead';
  patientId: number;
  patientName: string;
  patientEmail: string | null;
  onSent: () => void;
}

// Turn the plain-text compose box into safe HTML for the branded email shell.
const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const toHtml = (text: string): string =>
  escapeHtml(text).replace(/\n/g, '<br />');

const ComposeEmailDialog: React.FC<Props> = ({
  open, onClose, entity = 'patient', patientId, patientName, patientEmail, onSent,
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const reset = () => { setSubject(''); setBody(''); };

  const handleSend = async () => {
    if (!patientEmail) return;
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim()) { toast.error('Message is required'); return; }
    try {
      setSending(true);
      const send = entity === 'lead'
        ? communicationService.sendLeadEmail
        : communicationService.sendEmail;
      await send(patientId, {
        subject: subject.trim(),
        body_html: toHtml(body),
      });
      toast.success('Email sent');
      reset();
      onSent();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to send email'));
    } finally {
      setSending(false);
    }
  };

  const title = `Send Email to ${patientName}${patientEmail ? `  ·  ${patientEmail}` : ''}`;

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={title}
      isLoading={sending}
      width="max-w-2xl"
      titleAlign="left"
      titleClassName="text-lg font-bold text-[#366A75] leading-tight"
    >
      <div className="space-y-4">
        {!patientEmail && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
            Patient does not have an email address
          </div>
        )}
        <div>
          <label className="block text-[#366A75] font-medium mb-2">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={sending || !patientEmail}
            className={POPUP_STYLES.input}
          />
        </div>
        <div>
          <label className="block text-[#366A75] font-medium mb-2">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={sending || !patientEmail}
            rows={10}
            className={`${POPUP_STYLES.input} resize-y`}
          />
        </div>
        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleSend}
            disabled={sending || !patientEmail}
            className={POPUP_STYLES.button}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
          <button onClick={onClose} disabled={sending} className={POPUP_STYLES.button}>
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default ComposeEmailDialog;
